export type LongwallsEnterprisePilotGate = {
  generatedAt: string;
  projectId: string;
  status: "pilot_ready" | "pilot_not_ready";
  score: number;
  summary: {
    greenRuns: number;
    requiredGreenRuns: number;
    contractReady: boolean;
    handoffReady: boolean;
    releaseDecision: string | null;
    rollbackReady: boolean;
    sloBaseline: {
      readinessScore: number;
      controlTowerScore: number;
      artifactAssuranceScore: number;
    };
  };
  blockers: string[];
  nextActions: string[];
  evidenceLinks: string[];
  gateBreakdown: Array<{
    id: string;
    label: string;
    status: "green" | "watch" | "risk";
    detail: string;
  }>;
};

export type LongwallsEnterpriseProofBoard = {
  generatedAt: string;
  status: "reference_ready" | "reference_building";
  summary: {
    referenceLotsReady: number;
    targetReferenceLots: number;
    sponsorReadyLots: number;
    finalizedLots: number;
    openBlockers: number;
    averageGateScore: number;
    nextMove: string;
    headline: string;
  };
  lots: Array<{
    projectId: string;
    customerId: string;
    code: string;
    name: string;
    projectStatus: string;
    gateStatus: "pilot_ready" | "pilot_not_ready";
    gateScore: number;
    chainState: string;
    greenRuns: number;
    requiredGreenRuns: number;
    blockerCount: number;
    nextMove: string;
    contractReady: boolean;
    handoffReady: boolean;
    releaseDecision: string | null;
  }>;
  repeatability: {
    status: "repeatable" | "building";
    comparisonHeadline: string;
    distinctArchetypes: number;
    distinctDomainFamilies: number;
    averageReuseRatio: number;
    averageCustomDeltaLoad: number;
    lots: Array<{
      projectId: string;
      code: string;
      name: string;
      archetypeId: string | null;
      domainFamily: string | null;
      brickCount: number;
      requiredBrickCount: number;
      primaryFlowCount: number;
      reuseRatio: number;
      customDeltaLoad: number;
    }>;
  };
};

export type LongwallsCompositionCatalog = {
  generatedAt: string;
  summary: {
    archetypeCount: number;
    brickCount: number;
    validatedDocuments: number;
    sourceRepoCount: number;
    candidateBrickCount: number;
    priorityPackCount: number;
    canonicalManifestCount: number;
    canonicalSecurityProfileCount: number;
    canonicalQualityProfileCount: number;
    canonicalCompatibilityEntryCount: number;
    canonicalImplementationCount: number;
    canonicalReuseContextCount: number;
    canonicalAssemblyBrickCount: number;
    canonicalGovernanceRuntimeCount: number;
    canonicalSharedBrickCount: number;
    canonicalJarvisAffineCount: number;
    differentiatedProfileCount: number;
    designVariantCount: number;
    uniqueDifferentiatedBrickCount: number;
  };
  archetypes: Array<{
    id: string;
    title: string;
    summary: string;
    layoutFamily: string;
    workflowFamily: string;
    visualThesis: string;
    experienceLabel: string;
    workspaceMode: string;
    density: "compact" | "balanced" | "immersive";
    motionProfile: "restrained" | "expressive";
    heroNarrative: string;
    securityDefaults: string[];
    moduleBadge: string;
    proofScenarios: string[];
    recommendedBrickIds: string[];
  }>;
  bricks: Array<{
    id: string;
    title: string;
    family: string;
    summary: string;
    compatibleArchetypes: string[];
    sensitive: boolean;
  }>;
  canonical: {
    releaseStatus: string;
    extractionStatus: string;
    manifests: Array<{
      id: string;
      title: string;
      owner: string;
      maturity: string;
      reusePotential: string;
      rolloutReadiness: string;
      consumptionMode: string;
      controlPlaneAffinity: string;
      targetPackage: string;
      sensitive: boolean;
      compatibleArchetypes: string[];
    }>;
    securityHighlights: Array<{
      brickId: string;
      classification: string;
      approvalRequired: boolean;
      secretHandling: string;
    }>;
    qualityHighlights: Array<{
      brickId: string;
      qaTiers: string[];
      playwrightCoverage: string;
      stabilityTarget: string;
    }>;
    compatibilityHighlights: Array<{
      brickId: string;
      targetPackage: string;
      archetypeCount: number;
      blockedByCount: number;
    }>;
    implementationHighlights: Array<{
      brickId: string;
      modulePath: string;
      exportPath: string;
      extractionStatus: string;
      reuseContextCount: number;
      qaStatus: string;
      securityStatus: string;
      uiStatus: string;
      runtimeSourcePackage: string | null;
      consumableInFoundry: boolean;
      consumableInJarvis: boolean;
    }>;
    governanceHighlights: Array<{
      brickId: string;
      title: string;
      consumptionMode: string;
      controlPlaneAffinity: string;
      runtimeSourcePackage: string | null;
      consumableInFoundry: boolean;
      consumableInJarvis: boolean;
    }>;
  };
  harvest: {
    sourceRepos: Array<{
      id: string;
      title: string;
      role: string;
      tier: string;
      worktreePosture: string;
      candidateCount: number;
    }>;
    priorityPacks: Array<{
      id: string;
      title: string;
      summary: string;
      sourceRepoIds: string[];
      targetPhase: string;
      status: string;
      candidateCount: number;
    }>;
    candidateHighlights: Array<{
      id: string;
      title: string;
      sourceRepoId: string;
      maturity: string;
      reusePotential: string;
      securityImpact: string;
    }>;
  };
  differentiation: {
    status: "differentiated" | "building";
    comparisonHeadline: string;
    distinctProfileCount: number;
    distinctDesignVariantCount: number;
    uniqueBrickCoverageCount: number;
    averageRecommendedBrickCount: number;
    families: Array<{
      archetypeId: string;
      title: string;
      profileId: string;
      designVariantId: string;
      recommendedBrickCount: number;
      premiumBrickCount: number;
      sourceRepoCount: number;
      attributeCount: number;
    }>;
  };
};

export type LongwallsEnterprisePilotPack = {
  generatedAt: string;
  projectId: string;
  projectName: string;
  summary: {
    status: "pilot_ready" | "pilot_not_ready";
    score: number;
    headline: string;
    sponsorNarrative: string;
    operatingModel: string;
    nextMove: string;
  };
  gate: {
    blockers: string[];
    nextActions: string[];
    evidenceLinks: string[];
    gateBreakdown: Array<{
      id: string;
      label: string;
      status: "green" | "watch" | "risk";
      detail: string;
    }>;
  };
  atlas: {
    posture: string;
    prioritizedRoadmapUrl: string;
    prioritizedRoadmapPdfUrl: string;
    nextMove: string;
  };
  foundry: {
    contractReady: boolean;
    handoffReady: boolean;
    releaseStatus: string;
    contractArtifact: string | null;
    handoffPackage: string | null;
    releaseBundleJson: string;
    releaseBundleMarkdown: string;
  };
  jarvis: {
    bridgeStatus: string;
    chainState: string;
    releaseDecision: string | null;
    pendingApprovals: number;
    openIncidents: number;
    founderLink: string | null;
    tacticalLink: string | null;
    operatorLink: string | null;
  };
  sloBaseline: {
    greenRuns: number;
    requiredGreenRuns: number;
    readinessScore: number;
    controlTowerScore: number;
    artifactAssuranceScore: number;
    openIncidents: number;
    pendingApprovals: number;
  };
  artifactUrls: {
    pilotPackJson: string;
    pilotPackMarkdown: string;
    roleGuide: string;
    supportRunbook: string;
    deploymentKit: string;
    executivePackJson: string;
    executivePackMarkdown: string;
    readinessPackJson: string;
    readinessPackMarkdown: string;
    releaseBundleJson: string;
    releaseBundleMarkdown: string;
  };
};

export type LongwallsLighthouseMotionPack = {
  schema: "longwalls.lighthouse-motion@1";
  generatedAt: string;
  readiness: "ready_for_sale" | "pilot_ready" | "building";
  summary: {
    headline: string;
    positioning: string;
    executiveNarrative: string;
    nextMove: string;
    executiveReadTimeMinutes: number;
  };
  story: {
    problem: string;
    prioritization: string;
    decisions: string;
    blueprint: string;
    assembly: string;
    proof: string;
  };
  commercialMapping: {
    sponsor: {
      title: string;
      primaryQuestion: string;
      whyNow: string;
      proofPoints: string[];
      caution: string;
    };
    buyer: {
      title: string;
      primaryQuestion: string;
      whyNow: string;
      proofPoints: string[];
      caution: string;
    };
    operator: {
      title: string;
      primaryQuestion: string;
      whyNow: string;
      proofPoints: string[];
      caution: string;
    };
  };
  offer: {
    name: string;
    duration: string;
    scope: string[];
    deliverables: string[];
    exclusions: string[];
  };
  proof: {
    referenceLotsReady: number;
    targetReferenceLots: number;
    sponsorReadyLots: number;
    averageGateScore: number;
    repeatabilityStatus: "repeatable" | "building";
    distinctArchetypes: number;
    distinctDomainFamilies: number;
    provenClaims: string[];
    partialClaims: string[];
    generalizationFrontier: string[];
    highlightedLots: Array<{
      projectId: string;
      code: string;
      name: string;
      gateStatus: "pilot_ready" | "pilot_not_ready";
      archetypeId: string | null;
      domainFamily: string | null;
      reuseRatio: number;
      customDeltaLoad: number;
      atlasUrl: string;
      foundryUrl: string;
      jarvisUrl: string;
      pilotPackUrl: string;
    }>;
  };
  demoScript: Array<{
    id: string;
    title: string;
    durationMinutes: number;
    goal: string;
    surface: string;
    talkingPoints: string[];
  }>;
  artifactUrls: {
    motionPackJson: string;
    preRead: string;
    demoScript: string;
    pilotOffer: string;
    roleGuideCommercial: string;
    evidencePack: string;
  };
};

export type LongwallsLiveAccountConversionPack = {
  schema: "longwalls.live-account-conversion@2";
  generatedAt: string;
  status: "benchmark_pending" | "benchmark_in_progress" | "pilot_go" | "pilot_no_go";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    motionDependency: string;
    passesClosed: number;
    totalPasses: number;
    boundedPilotReadinessPercent: number;
    liveLaunchStatus: "ready" | "watch" | "blocked";
  };
  account: {
    name: string;
    benchmarkApplication: string;
    benchmarkRepoPath: string;
    benchmarkStack: string[];
    benchmarkRoles: string[];
    sponsorRole: string;
    buyerRole: string;
    operatorRole: string;
  };
  benchmark: {
    sourceOfTruth: string;
    boundedMvpScope: string[];
    excludedScope: string[];
  };
  passes: Array<{
    id: "pass_a" | "pass_b" | "pass_c";
    title: string;
    status: "pending" | "ready_to_execute" | "in_progress" | "closed";
    goal: string;
    deliverables: string[];
    blockers: string[];
    successCriteria: string[];
  }>;
  comparison: {
    dimensions: Array<{
      id: string;
      title: string;
      weight: number;
      description: string;
    }>;
    decisionRule: string;
    notes: string[];
    rerun: {
      absorbedLots: Array<{
        lotId: "lot_12" | "lot_13" | "lot_14" | "lot_15";
        title: string;
        status: "absorbed" | "partial" | "missing";
        impact: string[];
      }>;
      weightedTotals: {
        manualExisting: number;
        engineBlind: number;
        engineReuseBaseline: number;
        engineRerun: number;
        goThreshold: number;
        gapToManualExisting: number;
      };
      verdict: "go" | "no_go";
      rationale: string[];
    };
  };
  pilot: {
    sliceName: string;
    businessIntent: string;
    inScope: string[];
    outOfScope: string[];
    successConditions: string[];
  };
  accountReadiness: {
    overallStatus: "ready" | "watch" | "blocked";
    readinessPercent: number;
    domains: Array<{
      id: string;
      title: string;
      status: "ready" | "watch" | "blocked";
      why: string;
      evidence: string[];
    }>;
  };
  pilotProtocol: {
    status: "ready" | "watch" | "blocked";
    launchMode: string;
    deploymentPreference: string;
    nextGate: string;
    phases: Array<{
      id: string;
      title: string;
      owner: string;
      status: "ready" | "watch" | "blocked";
      objective: string;
      entryCriteria: string[];
      exitCriteria: string[];
      proof: string[];
    }>;
  };
  rollback: {
    strategy: string;
    triggers: string[];
    actions: string[];
  };
  successScorecard: {
    northStar: string;
    metrics: Array<{
      id: string;
      title: string;
      target: string;
      source: string;
      owner: string;
      status: "defined" | "watch" | "blocked";
    }>;
  };
  goNoGo: {
    status: "go" | "no_go";
    rationale: string;
    requiredToReachGo: string[];
    threshold: string[];
  };
  artifactUrls: {
    conversionPackJson: string;
    blindBrief: string;
    blindScorecard: string;
    reuseDelta: string;
    comparisonScorecard: string;
    pilotScope: string;
    riskRegister: string;
    goNoGo: string;
    pilotProtocol: string;
    executionChecklist: string;
    rollbackPlan: string;
    successScorecard: string;
  };
};

export type LongwallsLivePilotProofPack = {
  schema: "longwalls.live-lighthouse-conversion@1";
  generatedAt: string;
  status: "go_bounded_local_live" | "hold";
  summary: {
    headline: string;
    narrative: string;
    nextGate: string;
    launchMode: "bounded_local_live" | "customer_runtime_verified";
    productionRuntimeVerified: boolean;
    boundedPilotReadinessPercent: number;
  };
  boundedPilot: {
    accountName: string;
    benchmarkApplication: string;
    sliceName: string;
    goNoGoStatus: "go" | "no_go";
    liveLaunchStatus: "ready" | "watch" | "blocked";
    nextGate: string;
    limitations: string[];
  };
  runtimeProof: {
    schema: "longwalls.live-pilot-runtime-proof@1";
    generatedAt: string;
    pilotId: string;
    pilotName: string;
    accountName: string;
    benchmarkApplication: string;
    summary: {
      status: "go_bounded_local_live" | "hold";
      headline: string;
      launchMode: "bounded_local_live" | "customer_runtime_verified";
      productionRuntimeVerified: boolean;
      passCount: number;
      watchCount: number;
      failCount: number;
      checklistExecuted: boolean;
      supportRunbookExecuted: boolean;
      rollbackExercised: boolean;
      attestationProduced: boolean;
    };
    runtimeLane: {
      dryRunReady: boolean;
      liveMutationStatus: "ready" | "watch" | "blocked";
      customerRuntimeStatus: "ready" | "watch" | "blocked";
      operatorTruthVisible: boolean;
    };
    healthChecks: Array<{
      id: string;
      label: string;
      status: "pass" | "watch" | "fail";
      summary: string;
      evidenceRefs: string[];
    }>;
    nextActions: string[];
    limitations: string[];
  };
  supportRunbook: {
    title: string;
    ownerLane: string;
    sourceDocument: string;
    executedAt: string;
    sections: string[];
    executedChecks: string[];
  };
  rollbackDrill: {
    schema: "longwalls.live-pilot-rollback-drill@1";
    generatedAt: string;
    status: "pass" | "fail";
    scenario: string;
    restoredControlPlane: string;
    steps: string[];
    evidenceRefs: string[];
  };
  releaseAttestation: {
    schema: "longwalls.live-pilot-attestation@1";
    generatedAt: string;
    status: "bounded_local_live_ready" | "hold";
    productionRuntimeVerified: boolean;
    assertions: string[];
    blockers: string[];
    evidenceRefs: string[];
  };
  artifactUrls: {
    packJson: string;
    runtimeProof: string;
    supportRunbook: string;
    rollbackDrill: string;
    releaseAttestation: string;
    conversionPackJson: string;
    executionChecklist: string;
    rollbackPlan: string;
    successScorecard: string;
  };
};

export type LongwallsCustomerRuntimeBridgePack = {
  schema: "longwalls.customer-runtime-bridge@1";
  generatedAt: string;
  status: "go" | "no_go";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    lot38GoNoGo: "go" | "no_go";
    customerRuntimeVerified: boolean;
    productionRuntimeVerified: false;
    previewUrl: string | null;
    runId: string | null;
    projectId: string | null;
  };
  liveAccountScopeFreeze: {
    status: "pass" | "fail";
    accountName: string;
    benchmarkApplication: string;
    scopeFreeze: {
      sliceName: string;
      inScope: string[];
      outOfScope: string[];
      runtimeOwner: {
        name: string;
        role: string;
        accountScope: string;
      };
      providerScope: {
        vercelProject: string;
        vercelTeam: string;
        previewUrl: string | null;
      };
      startingPointCustomerRuntimeStatus: "ready" | "watch" | "blocked";
    };
    checks: Array<{
      id: string;
      label: string;
      status: "pass" | "fail";
      summary: string;
    }>;
    evidenceRefs: string[];
  };
  budgetGuardrails: {
    schema: "longwalls.customer-runtime-budget-guardrails@1";
    generatedAt: string;
    status: "pass" | "fail";
    caps: {
      previewDeploysPerRun: number;
      previewDeployTarget: "bounded_preview_only";
      remoteJarvisRuntime: "disabled_by_default";
      runtimeSecretsForwardedToPreview: false;
    };
    protections: {
      isolatedJarvisRuntimeByDefault: boolean;
      previewDeployOptInByDefault: boolean;
      runtimeSecretsForwardedToPreview: boolean;
      readmeWarningsPresent: boolean;
      rollbackPrepared: boolean;
    };
    checks: Array<{
      id: string;
      label: string;
      status: "pass" | "fail";
      summary: string;
      evidenceRefs: string[];
    }>;
  };
  shadowRun: {
    schema: "longwalls.customer-runtime-shadow-run@1";
    generatedAt: string;
    status: "pass" | "fail";
    laneType: "controlled_real_account_shadow_run";
    customerRuntimeVerified: boolean;
    productionRuntimeVerified: false;
    previewUrl: string | null;
    inspectUrl: string | null;
    runId: string | null;
    projectId: string | null;
    operatorTruthVisible: boolean;
    endpointChecks: Array<{
      id: string;
      status: "passed" | "failed";
      httpStatus: number | null;
    }>;
    evidenceRefs: string[];
    note: string;
  };
  rollbackDrill: {
    schema: "longwalls.customer-runtime-rollback-drill@1";
    generatedAt: string;
    status: "pass" | "fail";
    executionMode: "dry_guarded" | "unknown";
    previewUrl: string | null;
    restoredControlPlane: string;
    rollbackCommandPrepared: boolean;
    note: string;
    evidenceRefs: string[];
  };
  supportDrill: {
    schema: "longwalls.customer-runtime-support-drill@1";
    generatedAt: string;
    status: "pass" | "fail";
    ownerLane: string;
    sourceDocument: string;
    executedChecks: string[];
    evidenceRefs: string[];
  };
  attestation: {
    schema: "longwalls.customer-runtime-attestation@1";
    generatedAt: string;
    status: "customer_runtime_verified" | "hold";
    customerRuntimeVerified: boolean;
    productionRuntimeVerified: false;
    assertions: string[];
    blockers: string[];
    limitations: string[];
    evidenceRefs: string[];
  };
  artifactUrls: {
    packJson: string;
    budgetGuardrails: string;
    shadowRun: string;
    rollbackDrill: string;
    supportDrill: string;
    attestation: string;
    conversionPackJson: string;
    livePilotPackJson: string;
  };
};

export type LongwallsProductionRuntimeVerificationPack = {
  schema: "longwalls.production-runtime-verification@1";
  generatedAt: string;
  status: "production_runtime_verified" | "hold";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    customerRuntimeVerified: boolean;
    productionRuntimeVerified: boolean;
    productionRuntimeScope: "longwalls_control_plane_only";
    productionTargetUrl: string | null;
    latestReadyProductionDeploymentId: string | null;
    passCount: number;
    failCount: number;
  };
  accountFreeze: {
    schema: "longwalls.production-runtime-account-freeze@1";
    status: "pass" | "fail";
    accountName: string;
    productionRuntimeScope: "longwalls_control_plane_only";
    productionDomains: string[];
    checks: Array<{ id: string; status: "pass" | "fail"; summary: string }>;
  };
  topology: {
    schema: "longwalls.production-runtime-topology@1";
    status: "pass" | "fail";
    productionTargetUrl: string | null;
    latestReadyProductionDeploymentId: string | null;
    previewUrl: string | null;
    checks: Array<{ id: string; status: "pass" | "fail"; summary: string }>;
  };
  envIam: {
    schema: "longwalls.production-runtime-env-iam@1";
    status: "pass" | "fail";
    authPosture: string;
    runtimeControls: Record<string, unknown>;
    checks: Array<{ id: string; status: "pass" | "fail"; summary: string }>;
  };
  backupRestore: {
    schema: "longwalls.production-runtime-backup-restore@1";
    status: "pass" | "fail";
    restoreMode: string;
    snapshots: Array<{ path: string; exists: boolean; bytes?: number; sha256: string | null }>;
    checks: Array<{ id: string; status: "pass" | "fail"; summary: string }>;
  };
  dataSafety: {
    schema: "longwalls.production-runtime-data-safety@1";
    status: "pass" | "fail";
    controls: Record<string, unknown>;
    checks: Array<{ id: string; status: "pass" | "fail"; summary: string }>;
  };
  liveWindow: {
    schema: "longwalls.production-runtime-live-window@1";
    status: "pass" | "fail";
    mode: string;
    endpointChecks: Array<{ id: string; url: string; method: string; status: number; expected: string; bytes: number }>;
  };
  rollbackDrill: {
    schema: "longwalls.production-runtime-rollback-drill@1";
    status: "pass" | "fail";
    executionMode: string;
    rollback: Record<string, unknown>;
    checks: Array<{ id: string; status: "pass" | "fail"; summary: string }>;
  };
  attestation: {
    status: "production_runtime_verified" | "hold";
    customerRuntimeVerified: boolean;
    productionRuntimeVerified: boolean;
    productionRuntimeScope: "longwalls_control_plane_only";
    assertions: string[];
    blockers: string[];
    limitations: string[];
  };
  releaseState: {
    productionRuntimeVerified: boolean;
    productionRuntimeScope: "longwalls_control_plane_only";
    lot42Unblocked: boolean;
    lot43RequiresGeneratedCustomerAppProdProof: boolean;
    nextGate: string;
  };
  limitations: string[];
  artifactUrls: Record<string, string>;
};

export type LongwallsLegacyReverseEngineeringPack = {
  schema: "longwalls.legacy-reverse-engineering@1";
  legacyIntelligenceVersion: "v2";
  generatedAt: string;
  status: "intake_ready" | "intake_building";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    repoCount: number;
    groundedRepoCount: number;
    apiSpecCount: number;
    proposedSectionCount: number;
    moduleCount: number;
    blockingGapCount: number;
    resetPhaseCount: number;
    promotionReadiness: "ready" | "watch" | "blocked";
  };
  repos: Array<{
    id: string;
    title: string;
    repoPath: string;
    status: "grounded" | "watch" | "missing";
    packageName: string | null;
    packageManager: string | null;
    domainHypothesis: string;
    sponsorNarrative: string;
    frameworkSignals: string[];
    integrationSignals: string[];
    runtimeSignals: string[];
    observedAssets: {
      docsCount: number;
      apiSpecCount: number;
      uiSurfaceCount: number;
      hasTests: boolean;
      hasPrisma: boolean;
      hasSupabase: boolean;
      hasPackageManifest: boolean;
    };
    foundryLift: {
      sectionIds: string[];
      confidenceScore: number;
      blocker: string | null;
    };
    reversePlan: {
      priority: "p0" | "p1";
      firstExtraction: string;
      why: string;
    };
    riskLevel: "low" | "watch" | "high";
    extractedModules: Array<{
      id: string;
      repoId: string;
      title: string;
      moduleType: "ui" | "api" | "data" | "auth" | "workflow" | "runtime" | "support" | "docs" | "qa";
      sourcePaths: string[];
      foundrySectionIds: string[];
      confidenceScore: number;
      reusePotential: "direct_reuse" | "rebuild_guided" | "reference_only";
      rationale: string;
      evidenceRefs: string[];
    }>;
    repoGapIds: string[];
  }>;
  proposedFoundrySections: Array<{
    id: string;
    title: string;
    sourceRepoIds: string[];
    autoFillReadiness: "high" | "medium" | "watch";
    rationale: string;
  }>;
  extractionProtocol: {
    status: "ready" | "building";
    phases: Array<{
      id: string;
      title: string;
      objective: string;
      deliverables: string[];
      exitCriteria: string[];
    }>;
  };
  moduleExtractionSummary: {
    status: "ready" | "watch" | "blocked";
    moduleCount: number;
    directReuseCount: number;
    rebuildGuidedCount: number;
    referenceOnlyCount: number;
    topModules: Array<{
      id: string;
      repoId: string;
      title: string;
      moduleType: "ui" | "api" | "data" | "auth" | "workflow" | "runtime" | "support" | "docs" | "qa";
      sourcePaths: string[];
      foundrySectionIds: string[];
      confidenceScore: number;
      reusePotential: "direct_reuse" | "rebuild_guided" | "reference_only";
      rationale: string;
      evidenceRefs: string[];
    }>;
  };
  gapAnalysis: {
    status: "ready" | "watch" | "blocked";
    headline: string;
    blockingGapCount: number;
    watchGapCount: number;
    repoGaps: Array<{
      id: string;
      repoId: string;
      severity: "blocking" | "watch";
      title: string;
      summary: string;
      recommendation: string;
      evidenceRefs: string[];
    }>;
    crossRepoGaps: Array<{
      id: string;
      severity: "blocking" | "watch";
      title: string;
      summary: string;
      impactedRepoIds: string[];
      recommendation: string;
    }>;
  };
  resetBlueprint: {
    status: "ready" | "watch" | "blocked";
    headline: string;
    phases: Array<{
      id: string;
      title: string;
      objective: string;
      sourceRepoIds: string[];
      deliverables: string[];
      exitCriteria: string[];
      riskReduction: string;
    }>;
    firstThirtyDays: string[];
    foundryPromotionCriteria: string[];
  };
  foundryPromotionBundle: {
    status: "ready" | "watch" | "blocked";
    headline: string;
    sectionBundles: Array<{
      id: string;
      title: string;
      sourceRepoIds: string[];
      autoFillReadiness: "high" | "medium" | "watch";
      rationale: string;
      moduleIds: string[];
    }>;
    autofillPatches: Array<{
      sectionId: string;
      sourceRepoIds: string[];
      moduleIds: string[];
      recommendation: string;
    }>;
    openQuestions: string[];
    nextWorkItems: string[];
  };
  benchmarkImpact: {
    whyThisMatters: string[];
    longwallsLeverage: string;
  };
  artifactUrls: {
    legacyReverseEngineeringJson: string;
    repoDossier: string;
    repoMatrix: string;
    foundryHandoff: string;
    resetBlueprint: string;
  };
};

export type LongwallsAtlasGeneralizationPack = {
  schema: "longwalls.atlas-generalization@2";
  generatedAt: string;
  status: "robustness_locked" | "ready_for_benchmark_rerun";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    dossierCount: number;
    domainFamilyCount: number;
    sourceRepoCount: number;
    stressCaseCount: number;
    minimumDefensibilityScore: number;
  };
  diversityPack: {
    title: string;
    version: 2;
    proofReplayCount: number;
    divergentExtensionCount: number;
    dossiers: Array<{
      id: string;
      title: string;
      sourceRepoId: string;
      repoPath: string;
      domainFamily: string;
      operatingModel: string;
      recommendedArchetypeId: string;
      groundingMode: "repo_replay" | "seeded_from_archetype_library";
      keySignals: string[];
      divergenceAxes: string[];
      stressCases: string[];
      scoringFactors: string[];
      sponsorQuestions: string[];
    }>;
  };
  scoringRobustness: {
    status: "robust_on_divergent_dossiers" | "watch";
    headline: string;
    dossierCount: number;
    domainFamilyCount: number;
    nonCousinDossierCount: number;
    divergenceAxisCount: number;
    stressCaseCount: number;
    averageDefensibilityScore: number;
    minimumDefensibilityScore: number;
    averageTransferabilityScore: number;
    dossierScores: Array<{
      dossierId: string;
      title: string;
      domainFamily: string;
      defensibilityScore: number;
      transferabilityScore: number;
      exportReadinessScore: number;
      confidence: "high" | "medium" | "watch";
      rationale: string[];
    }>;
  };
  outputPackV2: {
    status: "locked";
    sponsorOutputs: string[];
    portfolioSignals: string[];
    founderReading: string;
    executiveAssertion: string;
  };
  exportContractV3: {
    status: "locked";
    requiredArtifacts: string[];
    requiredDecisionFields: string[];
    blockingConditions: string[];
    foundryMapping: Record<string, string>;
    recommendationContract: {
      requiredQuestions: string[];
      requiredRecommendationFields: string[];
      rationaleField: string;
    };
  };
  benchmarkImpact: {
    lot11Dependency: string;
    whyThisMatters: string[];
  };
  artifactUrls: {
    atlasGeneralizationJson: string;
    diversityPackV2: string;
    scoringRobustness: string;
    executivePortfolioPackJson: string;
    executivePortfolioPackMarkdown: string;
    exportContractV3: string;
  };
};

export type LongwallsFoundryGeneralizationPack = {
  schema: "longwalls.foundry-generalization@2";
  generatedAt: string;
  status: "ready_for_external_project_rerun";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    ingestionLaneCount: number;
    topologyScenarioCount: number;
    completenessSectionCount: number;
    externalScenarioCount: number;
    blockingConditionCount: number;
  };
  externalIngestion: {
    status: "locked";
    lanes: Array<{
      id: string;
      title: string;
      githubMode: string;
      projectType: string;
      summary: string;
      requiredSignals: string[];
    }>;
  };
  topologyAndCompliance: {
    status: "locked";
    scenarios: Array<{
      id: string;
      title: string;
      topology: string;
      complianceProfile: string;
      auth: string;
      observability: string;
      requiredControls: string[];
    }>;
  };
  buildReadyRigor: {
    status: "locked";
    blockerFamilies: Array<{
      id: string;
      title: string;
      source: string;
      severity: "blocking" | "watch";
      trigger: string;
      requiredResolution: string;
    }>;
    benchmarkSignals: string[];
    contractStatus: "build-ready" | "needs-input" | "blocked";
    contractScore: number;
    runtimeConstraintCount: number;
    productionBlockerCount: number;
  };
  benchmarkImpact: {
    lot11Dependency: string;
    whyThisMatters: string[];
  };
  artifactUrls: {
    foundryGeneralizationJson: string;
    readinessMatrix: string;
    foundryContractV3: string;
    completenessValidator: string;
    externalIngestionScorecard: string;
  };
};

export type LongwallsJarvisDepthPack = {
  schema: "longwalls.jarvis-depth@1";
  generatedAt: string;
  status: "depth_building" | "depth_proof_locked";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    referenceLots: number;
    averageDepthScore: number;
    averageDivergenceScore: number;
    averageCustomDeltaLoad: number;
  };
  depthScorecard: {
    status: "locked" | "building";
    generatedAppsValidated: number;
    distinctStructuralSignatures: number;
    averageReuseRatio: number;
    minimumDepthScore: number;
    minimumDivergenceScore: number;
    runtimeValidatedCount: number;
    compositionFallbackCount: number;
  };
  customDeltaTrace: {
    status: "locked" | "building";
    headline: string;
    categoriesCovered: string[];
    lots: Array<{
      projectId: string;
      code: string;
      name: string;
      customDeltaLoad: number;
      distinguishingAttributes: string[];
    }>;
  };
  generatedAppDivergence: {
    status: "locked" | "building";
    headline: string;
    structuralSignatures: string[];
    lots: Array<{
      projectId: string;
      code: string;
      name: string;
      archetypeId: string | null;
      domainFamily: string | null;
      structuralSignature: string;
      depthScore: number;
      divergenceScore: number;
      reuseRatio: number;
      customDeltaLoad: number;
      primaryFlowCount: number;
      distinguishingAttributes: string[];
    }>;
  };
  artifactUrls: {
    jarvisDepthJson: string;
    scorecard: string;
    customDeltaTrace: string;
    divergenceMatrix: string;
  };
};

export type LongwallsConciergerieAssemblyPack = {
  schema: "longwalls.conciergerie-assembly@1";
  generatedAt: string;
  status: "assembly_building" | "assembly_proof_locked";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    roleSurfaceCount: number;
    primaryFlowCount: number;
    dataEntityCount: number;
    customDeltaLoad: number;
    depthScore: number;
    divergenceScore: number;
  };
  foundrySource: {
    projectId: string;
    repoId: string;
    repositoryName: string;
    status: string;
    confidenceScore: number;
    requiredEnvKeys: string[];
    integrationTargets: string[];
    openQuestions: string[];
  };
  assemblyContract: {
    archetypeId: string;
    differentiationProfileId: string;
    designVariantId: string;
    structuralSignature: string;
    domainFamily: "operational_app";
    operatingModel: "workflow_orchestration";
    deliveryMode: "local-runnable";
    recommendedBrickIds: string[];
    requiredBrickIds: string[];
    premiumBrickIds: string[];
    securityDefaults: string[];
    qualityGates: string[];
  };
  roleSurfaces: Array<{
    role: "requester" | "dispatcher" | "runner" | "admin";
    title: string;
    surface: string;
    primaryQuestion: string;
    primaryFlows: string[];
    visibleData: string[];
    actions: string[];
    uxDifferentiators: string[];
  }>;
  flowGraph: Array<{
    id: string;
    title: string;
    ownerRole: "requester" | "dispatcher" | "runner" | "admin";
    supportingRoles: Array<"requester" | "dispatcher" | "runner" | "admin">;
    entrySignal: string;
    exitEvidence: string;
    requiredBricks: string[];
  }>;
  dataModel: {
    entities: Array<{
      id: string;
      title: string;
      ownerRole: "requester" | "dispatcher" | "runner" | "admin" | "shared";
      auditLevel: "standard" | "signed" | "sensitive";
      purpose: string;
    }>;
    relationships: string[];
  };
  customDelta: {
    status: "accepted";
    summary: string;
    load: number;
    domainRules: string[];
    customUiRequirements: string[];
    customIntegrations: string[];
    supportAssets: string[];
  };
  generatedApp: {
    appName: string;
    packageName: string;
    outputDir: string;
    archivePath: string;
    smokeStatus: "passed" | "failed";
    smokeResults: Array<{ command: string; code: number | null }>;
  };
  targetComparison: {
    target: "Conciergerie 2.0";
    verdict: "go_for_superiority_benchmark" | "repair_required";
    nonGenericSignals: string[];
    preservedTargetSignals: string[];
    assemblyBlockers: string[];
    runtimeOnlyGaps: string[];
  };
  artifactUrls: {
    assemblyJson: string;
    roleSurfaceMap: string;
    customDeltaReport: string;
    targetComparison: string;
    generatedAppManifest: string;
  };
};

export type LongwallsJarvisDefaultUsagePack = {
  schema: "longwalls.jarvis-default-usage@2";
  generatedAt: string;
  status: "default_usage_building" | "default_usage_locked";
  summary: {
    headline: string;
    narrative: string;
    nextMove: string;
    defaultSurface: "jarvis";
    roomCoverageScore: number;
    decisionLatencyReductionPercent: number;
    commandCoverageScore: number;
    controlTowerScore: number;
  };
  roleRoutines: Array<{
    role: "founder" | "tactical" | "operator";
    room: string;
    roomUrl: string | null;
    defaultQuestion: string;
    cadence: string;
    requiredSignals: string[];
    primaryActions: string[];
    fallbackIfMissing: string;
    readiness: "ready" | "watch" | "blocked";
  }>;
  decisionLatency: {
    status: "measured_proxy" | "building";
    baselineSurfaceCount: number;
    currentSurfaceCount: number;
    baselineReviewMinutes: number;
    currentReviewMinutes: number;
    reductionPercent: number;
    rationale: string[];
  };
  reviewRoutines: {
    status: "locked" | "building";
    matrix: Array<{
      role: "founder" | "tactical" | "operator";
      trigger: string;
      firstSurface: string;
      secondSurface: string;
      decisionOutput: string;
      evidenceRequired: string[];
    }>;
  };
  controlTower: {
    status: "locked" | "watch" | "building" | "blocked";
    headline: string;
    commandCoverageScore: number;
    loops: Array<{
      id: "founder_review" | "tactical_replay" | "operator_runtime" | "pilot_governance";
      title: string;
      owner: "founder" | "tactical" | "operator" | "cross_room";
      trigger: string;
      firstSurface: string;
      supportingSurface: string;
      readiness: "ready" | "watch" | "blocked";
      actions: string[];
      proofObjects: string[];
      blockerIfMissing: string;
    }>;
    rationale: string[];
  };
  pilotGovernance: {
    status: "locked" | "watch" | "blocked";
    headline: string;
    reviewStartSurface: string;
    commandPath: string[];
    requiredChecks: string[];
    freezeConditions: string[];
    allowedExits: string[];
  };
  surfacePolicy: {
    defaultSurface: "jarvis";
    prohibitedExits: string[];
    allowedEscalations: string[];
    requiredProofBeforeExit: string[];
  };
  operatingGuardrails: {
    noGoConditions: string[];
    evidenceRules: string[];
    escalationRules: string[];
  };
  artifactUrls: {
    defaultUsageJson: string;
    playbook: string;
    routinesMatrix: string;
    latencyScorecard: string;
    controlTower: string;
    pilotGovernance: string;
  };
};

export type LongwallsPilotFeedbackPacket = {
  id: string;
  author: string;
  summary: string;
  requestedChanges: string[];
  acceptanceNotes: string[];
  risks: string[];
  createdAt: string;
};

export type LongwallsPilotChainRecord = {
  projectId: string;
  state:
    | "not_started"
    | "bootstrapped"
    | "v1_running"
    | "v1_ready"
    | "feedback_received"
    | "finalizing"
    | "finalized"
    | "blocked";
  createdAt: string;
  updatedAt: string;
  forceBootstrap: boolean;
  contractDraft: { id: string } | null;
  handoff: { id: string; status: string; artifactUrls?: { handoffPackage?: string } } | null;
  v1Run: { id: string; status: string; decision: string | null; artifactUrls?: { deliveryResult?: string } } | null;
  finalizationRun: { id: string; status: string; decision: string | null; artifactUrls?: { deliveryResult?: string } } | null;
  latestFeedback: LongwallsPilotFeedbackPacket | null;
  latestRuns: Array<{ id: string; status: string; decision: string | null }>;
  history: Array<{
    id: string;
    type: "bootstrap" | "run_v1" | "feedback" | "finalize";
    createdAt: string;
    detail: string;
  }>;
  readyForFeedback: boolean;
  finalizationReady: boolean;
  nextAction: string;
};

type Envelope<TPayload, TKey extends string> = {
  ok?: boolean;
  error?: string;
} & Partial<Record<TKey, TPayload>>;

function isTransientLongwallsFetchError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return /failed to fetch|networkerror|load failed/i.test(error.message);
}

async function readEnvelope<TPayload, TKey extends string>(
  input: RequestInfo | URL,
  key: TKey,
  init?: RequestInit,
): Promise<TPayload> {
  for (let attempt = 0; attempt < 2; attempt += 1) {
    try {
      const response = await fetch(input, init);
      const payload = (await response.json().catch(() => null)) as Envelope<TPayload, TKey> | null;

      if (!response.ok || payload?.ok === false || payload?.[key] === undefined) {
        throw new Error(payload?.error ?? `Failed to load ${key} (status ${response.status})`);
      }

      return payload[key] as TPayload;
    } catch (error) {
      if (attempt === 0 && isTransientLongwallsFetchError(error)) {
        continue;
      }
      throw error;
    }
  }

  throw new Error(`Failed to load ${String(key)}`);
}

async function postEnvelope<TPayload, TKey extends string>(
  input: RequestInfo | URL,
  key: TKey,
  body?: unknown,
): Promise<TPayload> {
  return readEnvelope<TPayload, TKey>(input, key, {
    method: "POST",
    headers: body === undefined ? undefined : { "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function fetchLongwallsPilotGate(projectId: string): Promise<LongwallsEnterprisePilotGate> {
  return readEnvelope(`/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-gate`, "pilotGate");
}

export async function fetchLongwallsEnterpriseProofBoard(): Promise<LongwallsEnterpriseProofBoard> {
  return readEnvelope("/api/longwalls/enterprise-proof", "proofBoard");
}

export async function fetchLongwallsLighthouseMotionPack(): Promise<LongwallsLighthouseMotionPack> {
  return readEnvelope("/api/longwalls/lighthouse-motion", "lighthouseMotion");
}

export async function fetchLongwallsLiveAccountConversionPack(): Promise<LongwallsLiveAccountConversionPack> {
  return readEnvelope("/api/longwalls/live-account-conversion", "liveAccountConversion");
}

export async function fetchLongwallsLivePilotProofPack(): Promise<LongwallsLivePilotProofPack> {
  return readEnvelope("/api/longwalls/live-pilot", "livePilot");
}

export async function fetchLongwallsCustomerRuntimeBridgePack(): Promise<LongwallsCustomerRuntimeBridgePack> {
  return readEnvelope("/api/longwalls/customer-runtime-bridge", "customerRuntimeBridge");
}

export async function fetchLongwallsProductionRuntimeVerificationPack(): Promise<LongwallsProductionRuntimeVerificationPack> {
  return readEnvelope("/api/longwalls/production-runtime-verification", "productionRuntimeVerification");
}

export async function fetchLongwallsLegacyReverseEngineeringPack(): Promise<LongwallsLegacyReverseEngineeringPack> {
  return readEnvelope("/api/longwalls/legacy-reverse-engineering", "legacyReverseEngineering");
}

export async function fetchLongwallsAtlasGeneralizationPack(): Promise<LongwallsAtlasGeneralizationPack> {
  return readEnvelope("/api/longwalls/atlas-generalization", "atlasGeneralization");
}

export async function fetchLongwallsFoundryGeneralizationPack(): Promise<LongwallsFoundryGeneralizationPack> {
  return readEnvelope("/api/longwalls/foundry-generalization", "foundryGeneralization");
}

export async function fetchLongwallsJarvisDepthPack(): Promise<LongwallsJarvisDepthPack> {
  return readEnvelope("/api/longwalls/jarvis-depth", "jarvisDepth");
}

export async function fetchLongwallsConciergerieAssemblyPack(): Promise<LongwallsConciergerieAssemblyPack> {
  return readEnvelope("/api/longwalls/conciergerie-assembly", "conciergerieAssembly");
}

export async function fetchLongwallsJarvisDefaultUsagePack(): Promise<LongwallsJarvisDefaultUsagePack> {
  return readEnvelope("/api/longwalls/jarvis-default-usage", "jarvisDefaultUsage");
}

export async function fetchLongwallsCompositionCatalog(): Promise<LongwallsCompositionCatalog> {
  return readEnvelope("/api/longwalls/composition/catalog", "catalog");
}

export async function fetchLongwallsPilotChain(projectId: string): Promise<LongwallsPilotChainRecord> {
  return readEnvelope(`/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-chain`, "pilotChain");
}

export async function fetchLongwallsPilotPack(projectId: string): Promise<LongwallsEnterprisePilotPack> {
  return readEnvelope(`/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-pack`, "pilotPack");
}

export async function bootstrapLongwallsPilotChain(projectId: string): Promise<LongwallsPilotChainRecord> {
  return postEnvelope(`/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-chain/bootstrap`, "pilotChain");
}

export async function runLongwallsPilotV1(projectId: string): Promise<LongwallsPilotChainRecord> {
  return postEnvelope(`/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-chain/run-v1`, "pilotChain");
}

export async function submitLongwallsPilotFeedback(
  projectId: string,
  body: {
    author?: string;
    summary: string;
    requestedChanges?: string[];
    acceptanceNotes?: string[];
    risks?: string[];
  },
): Promise<LongwallsPilotChainRecord> {
  return postEnvelope(`/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-chain/feedback`, "pilotChain", body);
}

export async function finalizeLongwallsPilotChain(projectId: string): Promise<LongwallsPilotChainRecord> {
  return postEnvelope(`/api/longwalls/projects/${encodeURIComponent(projectId)}/pilot-chain/finalize`, "pilotChain");
}
